/* eslint-disable no-underscore-dangle */
/* eslint-disable no-useless-escape */
import {
  AfterViewInit, Component,
  EventEmitter, Input,
  OnDestroy, OnInit,
  Output, ViewChild,
  ViewEncapsulation, ElementRef,
  SimpleChanges, OnChanges,
  TemplateRef, Self, Optional
} from '@angular/core';
import {FormControl, NgControl} from '@angular/forms';
import {ReplaySubject, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {MatSelect} from '@angular/material/select';
import {FilteredItem} from '../../models/filtered-item.model';
import {MessageService} from '../../services/message-service.service';
import {FocusMonitor} from '@angular/cdk/a11y';
import isEqual from 'lodash/isEqual';
import {MatFormField} from '@angular/material/form-field';
import {UserModel} from '../../models/user.model';

export interface SelectSearchDropdownOption<T = any> {
  label: string;
  value: T;
  level?: number;
  parent?: any;
  extras?: string[];
  disabled?: boolean;
}
export type SelectSearchDropdownOptionsInput<T = any> = string | SelectSearchDropdownOption<T>;

type Comparator = (a: any, b: any) => boolean;
export const exactComparator: Comparator = (a: any, b: any) => a === b;
export const mdmIdComparator: Comparator = (a: any, b: any) => {
  if (a?.mdmId && b?.mdmId) {
    return a.mdmId === b.mdmId;
  }
  return false;
};

export const userNameComparator: Comparator = (a: UserModel, b: UserModel) => a?.userName === b?.userName;
export const isEqualComparator: Comparator = (a: any, b: any) => isEqual(a, b);
export const optionValueComparatorFactory =
  (optionName: string) =>
  (a: any, b: any): boolean =>
    typeof a !== 'string' ? a[optionName] === b : a === b;

export const DEFAULT_COMPARATORS = [exactComparator, mdmIdComparator, isEqualComparator];

@Component({
  selector: 'app-select-search-dropdown',
  templateUrl: './select-search-dropdown.component.html',
  styleUrls: ['./select-search-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SelectSearchDropdownComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  public normalizedItemList: SelectSearchDropdownOption[];

  @Input() useCustomToggler = false;
  @Input() useSelectAll = false;
  @Input() label: string = null;
  @Input() isRequired = false;
  @Input() isMultiple = false;
  @Input() testId = null;
  @Input() autoFocus = false;
  @Input() disableOptionCentering = true;
  @Input() comparators: Comparator[] = DEFAULT_COMPARATORS;
  @Input() canReset = true;

  @Input() public set itemsList(itemsList: SelectSearchDropdownOptionsInput[]) {
    const normalizedItemList = itemsList.map(il => (typeof il === 'string' ? {label: il, value: il} : il));
    const itemListValues = itemsList.map(il => (typeof il === 'string' ? il : il.value));
    /**
     * Reset selected values if they are absent in a new option list
     */
    if (Array.isArray(this.itemsFormCtrl.value)) {
      const boundedValue = this.itemsFormCtrl.value.filter(val => itemListValues.includes(val));
      const hasChange = this.itemsFormCtrl.value.length !== boundedValue.length;
      /**
       * Update only in case of value has been bounded
       */
      if (hasChange) {
        this.itemsFormCtrl.setValue(boundedValue);
        this.onActionSave.emit(this.itemsFormCtrl.value);
        this.valueChange.emit(this.itemsFormCtrl.value);
      }
    }
    this.normalizedItemList = normalizedItemList;
    this.filteredItemsList$.next(this.normalizedItemList);
  }

  @ViewChild('ItemsAssignSelect') public select: ElementRef<HTMLSelectElement>;
  @ViewChild('ItemsAssignSelect', {static: true}) ItemsAssignSelect: MatSelect;
  @ViewChild('matFormField', {read: MatFormField, static: true}) matFormField: MatFormField;
  @Input() hint: TemplateRef<any>;
  @Input() hintParams: any = {};
  // tslint:disable-next-line:no-output-on-prefix
  @Output() private onActionSave: EventEmitter<string[]> = new EventEmitter();

  private _itemsFormCtrl = new FormControl([]);
  /** control for the selected items for multi-selection checkboxes */
  @Input() set itemsFormCtrl(itemsFormCtrl: FormControl) {
    this._itemsFormCtrl = itemsFormCtrl;
  }
  get itemsFormCtrl(): FormControl {
    if (this.ctrl?.control) {
      return this.ctrl?.control as FormControl;
    }
    return this._itemsFormCtrl;
  }

  @Input() public set value(value: any[]) {
    this.itemsFormCtrl.setValue(value || []);
  }

  public get value(): any[] {
    return this.itemsFormCtrl.value;
  }

  @Output() public valueChange = new EventEmitter<any[]>();

  /** control for the MatSelect filter keyword multi-selection */
  public ItemsFormFilterCtrl: FormControl = new FormControl();

  /** list of items filtered by search keyword */
  public filteredItemsList$: ReplaySubject<FilteredItem[]> = new ReplaySubject<FilteredItem[]>(1);

  public isDropdownOpened: boolean;

  /** Subject that emits when the component has been destroyed. */
  // tslint:disable-next-line:variable-name
  private _onDestroy = new Subject<void>();

  constructor(
    private messageService: MessageService,
    private fm: FocusMonitor,
    private elRef: ElementRef<HTMLElement>,
    @Self() @Optional() private ctrl: NgControl
  ) {
    if (this.ctrl) {
      this.ctrl.valueAccessor = this;
    }
  }

  writeValue(obj: unknown): void {
    this.ItemsAssignSelect.writeValue(obj);
  }
  registerOnChange(fn: () => void): void {
    this.ItemsAssignSelect.registerOnChange(fn);
  }
  registerOnTouched(fn: () => object): void {
    this.ItemsAssignSelect.registerOnTouched(fn);
  }
  setDisabledState?(isDisabled: boolean): void {
    this.ItemsAssignSelect.setDisabledState(isDisabled);
  }

  ngOnInit(): void {
    // load the initial items list
    this.filteredItemsList$.next(this.normalizedItemList);

    // listen for search field value changes
    this.ItemsFormFilterCtrl.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterItemsMulti();
    });
  }

  reset(): void {
    this.itemsFormCtrl.setValue(null);
    this.onActionSave.emit(this.itemsFormCtrl.value);
    this.valueChange.emit(this.itemsFormCtrl.value);
  }

  ngAfterViewInit(): void {
    this.setInitialValue();
    if (this.autoFocus) {
      setTimeout(() => {
        this.fm.focusVia(this.select, 'program');
        this.fm.stopMonitoring(this.elRef.nativeElement);
      }, 0);
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.itemsFormCtrl) {
      this.trackDisabledStateForItemsCtrl(changes.itemsFormCtrl.currentValue);
    }
  }

  private trackDisabledStateForItemsCtrl(control: FormControl) {
    (this.matFormField as any)?._changeDetectorRef.markForCheck();
    this.ItemsAssignSelect.setDisabledState(control?.disabled);
    control?.registerOnDisabledChange(isDisabled => {
      this.ItemsAssignSelect.setDisabledState(isDisabled);
      (this.matFormField as any)?._changeDetectorRef.markForCheck();
    });
  }

  /**
   * Sets the initial value after the filteredItems are loaded initially
   */
  private setInitialValue(): void {
    this.filteredItemsList$.pipe(take(1), takeUntil(this._onDestroy)).subscribe(() => {
      // setting the compareWith property to a comparison function
      // triggers initializing the selection according to the initial value of
      // the form control (i.e. _initializeSelection())
      // this needs to be done after the filteredItems are loaded initially
      // and after the mat-option elements are available
      this.ItemsAssignSelect.compareWith = (a: any, b: any) => this.comparators.some(comparator => comparator(a, b));
    });
  }

  private filterItemsMulti(): void {
    if (!this.normalizedItemList) {
      return;
    }
    // get the search keyword
    let search: string = this.ItemsFormFilterCtrl.value;
    if (!search) {
      const filteredItemsNoSearch: FilteredItem[] = this.normalizedItemList.map(item => ({
        value: item.value,
        label: item.label.replace(/(<([^>]+)>)/gi, ''),
        level: item.level,
        disabled: item.disabled
      }));

      this.filteredItemsList$.next(filteredItemsNoSearch);

      return;
    }
    search = search.toLowerCase();

    // prefiltered items
    const prefilteredItems = this.normalizedItemList.filter(
      filteredItem =>
        filteredItem.label
          .replace(/(<([^>]+)>)/gi, '')
          .toLowerCase()
          .indexOf(search) > -1
    );

    // prefiltered items
    const prefilteredByExtrasItems = this.normalizedItemList.filter(filteredItem =>
      (filteredItem.extras || []).some(
        e =>
          e
            .replace(/(<([^>]+)>)/gi, '')
            .toLowerCase()
            .indexOf(search) > -1
      )
    );

    // filter the items
    const filteredItems: FilteredItem[] = this.normalizedItemList
      .map(item => {
        if (prefilteredItems.indexOf(item) > -1 || prefilteredItems.findIndex(pfi => pfi.parent === item) > -1) {
          return ['label', item] as const;
        }
        if (prefilteredByExtrasItems.indexOf(item) > -1 || prefilteredByExtrasItems.findIndex(pfi => pfi.parent === item) > -1) {
          return ['extras', item] as const;
        }
        return null;
      })
      .filter(filteredItem => filteredItem != null)
      .map(([found, item]) => {
        const filteredItem = {
          value: item.value,
          label: item.label.replace(/(<([^>]+)>)/gi, ''),
          level: item.level,
          disabled: item.disabled
        };

        const regex = new RegExp(search.replace(/[-\[\]\/\{\}\(\)\*\+\?\.\\\^\$\|]/g, '\\$&'), 'i');
        filteredItem.label = '' + item.label.replace(regex, '<b>$&</b>');
        if (found === 'extras') {
          if (item.extras != null) {
            const foundExtras = item.extras.filter(e => e.indexOf(search) > -1).slice(0, 2);
            if (foundExtras.length > 0) {
              filteredItem.label = filteredItem.label + ` (${foundExtras.map(fe => fe.replace(regex, '<b>$&</b>')).join(', ')})`;
            }
          }
        }
        return filteredItem;
      });

    this.filteredItemsList$.next(filteredItems);
  }

  public removeAllSelectedItems(event: Event): void {
    event.stopPropagation();
    this.itemsFormCtrl.setValue([]);
    this.ItemsFormFilterCtrl.setValue('');
    this.onActionSave.emit(this.itemsFormCtrl.value);
    this.valueChange.emit(this.itemsFormCtrl.value);
    (this.ItemsAssignSelect as unknown as any)._propagateChanges(); // refreshed the MatOption state
  }

  public toggleSelectAll($event: boolean) {
    if (!$event) {
      this.itemsFormCtrl.setValue([]);
      this.onActionSave.emit(this.itemsFormCtrl.value);
      this.valueChange.emit(this.itemsFormCtrl.value);
      (this.ItemsAssignSelect as unknown as any)._propagateChanges(); // refreshed the MatOption state
    } else {
      this.filteredItemsList$.pipe(take(1)).subscribe(fil => {
        this.itemsFormCtrl.setValue(fil.map(i => i.value));
        this.onActionSave.emit(this.itemsFormCtrl.value);
        this.valueChange.emit(this.itemsFormCtrl.value);
        fil.forEach(i => {
          (this.ItemsAssignSelect as unknown as any)._propagateChanges(i);
        });
      });
    }
  }

  public saveSelectedItems() {
    this.ItemsAssignSelect.close();
  }

  public openedChange(opened: boolean) {
    this.isDropdownOpened = opened;

    if (!opened) {
      this.onActionSave.emit(this.itemsFormCtrl.value);
      this.valueChange.emit(this.itemsFormCtrl.value);
    }
  }
  itemTrackFn(i: number, option: {label: string; value: any}) {
    return option.value;
  }

  public get errorMessage(): string {
    if (this.itemsFormCtrl?.invalid) {
      return this.messageService.getControlErrorMessage(this.itemsFormCtrl.errors);
    }
    return '';
  }
}
